import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteValue,
  CircularProgress,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { compact, filter, find, isArray, isEmpty, map, orderBy } from 'es-toolkit/compat';
import pluralize from 'pluralize';
import React, { ReactElement } from 'react';

import { AnnotationAssignment } from 'interfaces/annotation';
import { StainingMethod } from 'interfaces/stainType';
import { useAnnotationAssignmentForExecution } from 'utils/useAnnotationAssignments';
import { CasesParams } from 'utils/useCasesParams/types';

type AnnotationAssignmentOrderByIteratee =
  | keyof AnnotationAssignment
  | string
  | ((assignment: AnnotationAssignment) => unknown);
type AnnotationAssignmentOrderByOrder = boolean | 'asc' | 'desc';

export interface AnnotationAssignmentAutocompleteProps<Multiple extends boolean | undefined = false>
  extends Omit<
    AutocompleteProps<AnnotationAssignment, Multiple, false, false>,
    'options' | 'renderInput' | 'isOptionEqualToValue' | 'loading' | 'value'
  > {
  casesParams?: CasesParams;
  slideStainType?: string;
  stainingMethod?: StainingMethod;
  skipProcedureParams?: boolean;
  selectedValue: Multiple extends true ? Array<number> : number; // value is the id/ids of the annotation assignment(s)
  renderInput?: AutocompleteProps<AnnotationAssignment, Multiple, false, false>['renderInput'];
  textFieldProps?: TextFieldProps;
  fetchAll?: boolean; // if true, fetches all annotation assignments, otherwise fetches only the ones for the current casesParams
  annotationAssignmentToGroup?: (assignment: AnnotationAssignment) => string;
  orderByParams?: [
    iteratees?: AnnotationAssignmentOrderByIteratee | AnnotationAssignmentOrderByIteratee[],
    orders?: AnnotationAssignmentOrderByOrder | AnnotationAssignmentOrderByOrder[]
  ];
  annotationAssignmentOptions?: AnnotationAssignment[]; // if provided, will use these options instead of fetching
}

export function AnnotationAssignmentAutocomplete<Multiple extends boolean | undefined = false>(
  props: AnnotationAssignmentAutocompleteProps<Multiple>
): ReactElement {
  const {
    casesParams,
    selectedValue,
    onChange,
    multiple,
    renderInput,
    slideStainType,
    stainingMethod,
    skipProcedureParams,
    textFieldProps,
    disabled,
    fetchAll,
    annotationAssignmentToGroup,
    orderByParams,
    annotationAssignmentOptions,
    ...autocompleteProps
  } = props;

  const { annotationAssignments: annotationAssignmentsFromQuery, isAssignmentIdsLoading } =
    useAnnotationAssignmentForExecution({
      casesParams: !skipProcedureParams ? casesParams : undefined,
      slideStainType,
      stainingMethod,
      skipProcedureParams,
      fetchAll,
      disabled: disabled || !isEmpty(annotationAssignmentOptions),
    });

  const annotationAssignments = annotationAssignmentOptions || annotationAssignmentsFromQuery;

  const buildSelectedAssignmentFallbackOption = (assignmentId: number): AnnotationAssignment =>
    ({
      annotationAssignmentId: assignmentId,
      name: '',
      todos: [],
      isGlobal: false,
      description: '',
      archived: false,
      assignmentPriority: 0,
      createdAt: new Date(0),
      createdBy: '',
    } as AnnotationAssignment);

  const getAssignmentOptionById = (assignmentId: number, assignments: AnnotationAssignment[]) =>
    find(assignments, { annotationAssignmentId: assignmentId }) || buildSelectedAssignmentFallbackOption(assignmentId);

  function convertIdsToAssignmentOption(
    value: Multiple extends true ? number[] : number,
    assignments: AnnotationAssignment[]
  ): AutocompleteValue<AnnotationAssignment, Multiple, false, false> {
    return value
      ? ((isArray(value)
          ? map(value, (assignmentId) => getAssignmentOptionById(assignmentId, assignments))
          : getAssignmentOptionById(value, assignments)) as any)
      : multiple
      ? ([] as AutocompleteValue<AnnotationAssignment, Multiple, false, false>)
      : null;
  }

  const assignmentValue: AutocompleteValue<AnnotationAssignment, Multiple, false, false> = convertIdsToAssignmentOption(
    selectedValue,
    annotationAssignments
  );

  const fetchedOptions = compact(
    orderByParams ? orderBy(annotationAssignments, ...orderByParams) : annotationAssignments || []
  );
  const selectedOptions = compact(
    (isArray(assignmentValue) ? assignmentValue : assignmentValue ? [assignmentValue] : []) as AnnotationAssignment[]
  );
  const fallbackSelectedOptions = filter(
    selectedOptions,
    (selectedAssignment) => !find(fetchedOptions, { annotationAssignmentId: selectedAssignment.annotationAssignmentId })
  );
  const options = [...fetchedOptions, ...fallbackSelectedOptions];

  return (
    <Autocomplete<AnnotationAssignment, Multiple, false, false>
      multiple={multiple}
      value={assignmentValue}
      loading={isAssignmentIdsLoading}
      disabled={disabled}
      disableCloseOnSelect
      options={options}
      isOptionEqualToValue={(option, val) => option?.annotationAssignmentId === val?.annotationAssignmentId}
      getOptionLabel={(option: AnnotationAssignment) =>
        `${option?.annotationAssignmentId}${option?.name ? ` - ${option.name}` : ''}`
      }
      onChange={onChange}
      groupBy={annotationAssignmentToGroup}
      renderInput={
        renderInput
          ? renderInput
          : (params) => (
              <TextField
                {...params}
                label={pluralize('Annotation Assignment', multiple ? 2 : 1)}
                InputProps={{
                  ...params.InputProps,

                  endAdornment: (
                    <>
                      {!disabled && isAssignmentIdsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                {...textFieldProps}
              />
            )
      }
      {...autocompleteProps}
    />
  );
}
